import { Button, Position } from '@consigli/facade';
import { useBuildingPackageId, useProjectId, useServiceName } from '@consigli/hooks';
import { ServiceName } from '@consigli/types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FiCheck, FiArrowRight } from 'react-icons/fi';

import { WorkspaceProvider } from '@/layouts/workspace-layout/use-workspace-context';
import { WorkspaceLayout } from '@/layouts/workspace-layout/workspace-layout';
import { Route } from '@/routes';

interface SpecificationCompleteProps {
  onReviewValues?: () => void;
  onProceed?: () => void;
  isLoading?: boolean;
}

export const SpecificationComplete: React.FC<SpecificationCompleteProps> = ({
  onReviewValues,
  onProceed,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const buildingPackageId = useBuildingPackageId();
  const serviceName = useServiceName();

  const buildingPackageTabs = useMemo(
    () => [
      {
        href: (_serviceName: ServiceName, _projectId: number, _serviceId: string) =>
          `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${buildingPackageId}/${Route.USER_REVIEW_SPEC_REQUIREMENTS}`,
        name: 'building-tabs.processing-spec',
      },
    ],
    [projectId, serviceName, buildingPackageId],
  );

  return (
    <WorkspaceProvider>
      <WorkspaceLayout customTabs={buildingPackageTabs}>
        <div className="pt-52 flex items-center justify-center py-8">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('specification-complete.title')}
              </h2>

              <p className="text-gray-600 mb-4">{t('specification-complete.description')}</p>

              <p className="text-gray-600 mb-8">{t('specification-complete.instruction')}</p>

              <div className="space-y-3">
                <Button
                  onClick={onReviewValues}
                  disabled={isLoading}
                  primary
                  icon={FiCheck}
                  iconPosition={Position.LEFT}
                  className="w-full px-6 py-3 text-base font-medium"
                >
                  {t('specification-complete.actions.review')}
                </Button>

                <Button
                  onClick={onProceed}
                  disabled={isLoading}
                  secondary
                  icon={FiArrowRight}
                  iconPosition={Position.RIGHT}
                  className="w-full px-6 py-3 text-base font-medium"
                >
                  {t('specification-complete.actions.trigger-load-calculation')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </WorkspaceLayout>
    </WorkspaceProvider>
  );
};
